import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CONTENT_STATUS, PRIORITY, VIDEO_TYPES, DESIGN_TYPES, label } from "@/lib/meta";
import { DailyWork } from "@/components/DailyWork";
import { PendingWorkTable } from "@/components/PendingWorkTable";
import { InProgressNow } from "@/components/InProgressNow";
import { UpcomingFestivals } from "@/components/UpcomingFestivals";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Play, CheckCircle2, Clock, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function WorkRow({ it, actions }) {
  return (
    <div className="rounded-md border border-zinc-100 p-3" data-testid={`pool-item-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">{it.title}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
            <span className="font-medium text-zinc-600">{it.client_name}</span>
            <span>· {fmtDate(it.date)}</span>
            <span className="capitalize">· {it.content_type}</span>
            {it.priority && it.priority !== "medium" && <Badge color={PRIORITY[it.priority]?.color}>{label(PRIORITY, it.priority)}</Badge>}
          </div>
        </div>
        <Badge color={CONTENT_STATUS[it.status]?.color}>{label(CONTENT_STATUS, it.status)}</Badge>
      </div>
      {actions && <div className="mt-2 flex gap-2">{actions}</div>}
    </div>
  );
}

function PoolSection({ title, icon: Icon, tone, items, empty, renderActions }) {
  const border = tone === "red" ? "border-red-200" : "border-zinc-200";
  return (
    <Card className={`rounded-lg ${border} shadow-sm`}>
      <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
        <Icon className="h-4 w-4 text-zinc-400" />
        <h3 className="font-heading text-base font-semibold">{title}</h3>
        <Badge color={tone === "red" ? "red" : "blue"}>{items.length}</Badge>
      </div>
      <div className="space-y-2 p-5">
        {items.length === 0 && <div className="py-4 text-center text-sm text-zinc-400">{empty}</div>}
        {items.map((it) => <WorkRow key={it.id} it={it} actions={renderActions ? renderActions(it) : null} />)}
      </div>
    </Card>
  );
}

export function PoolWorkspace({ kind }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isVideo = kind === "video";
  const endpoint = isVideo ? "/pool/video" : "/pool/design";
  const qkey = isVideo ? "pool-video" : "pool-design";
  const { data, isLoading } = useQuery({ queryKey: [qkey], queryFn: async () => (await api.get(endpoint)).data });

  const refresh = () => { qc.invalidateQueries({ queryKey: [qkey] }); qc.invalidateQueries({ queryKey: ["my-day"] }); };
  const setStatus = async (id, status, msg) => { await api.put(`/content/${id}`, { status }); toast.success(msg); refresh(); };

  if (isLoading || !data)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>;

  const startBtn = (it) => <Button size="sm" onClick={() => setStatus(it.id, "in_progress", "Started — added to My In Progress")} data-testid={`start-${it.id}`}><Play className="mr-1 h-3 w-3" />Start Work</Button>;
  const completeBtn = (it) => <Button size="sm" variant="outline" onClick={() => setStatus(it.id, "ready_for_approval", "Marked ready for approval")} data-testid={`complete-${it.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Mark Completed</Button>;
  const undoBtn = (it) => <Button size="sm" variant="ghost" className="px-2 text-zinc-400 hover:text-zinc-700" onClick={() => setStatus(it.id, "planned", "Reverted to pending")} title="Undo start" data-testid={`undo-${it.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>;
  const isBoss = user?.role === "super_admin" || user?.role === "manager";
  const ownedByMe = (it) => isBoss || it.started_by_id === user?.id;
  const poolActions = (it) => it.status === "in_progress"
    ? (ownedByMe(it) ? <>{completeBtn(it)}{undoBtn(it)}</> : <span className="text-xs italic text-zinc-400" data-testid={`locked-${it.id}`}>In progress · {it.picked_by || it.started_by || "someone"}</span>)
    : <>{startBtn(it)}</>;

  const title = isVideo ? "Video Work Pool" : "Design Work Pool";
  const sub = isVideo ? "Pick any pending video and start editing — the pool is shared." : "Everything that needs designing. Start work and mark it ready for approval.";
  const c = data.counts;

  return (
    <div className="space-y-6" data-testid={`${kind}-pool`}>
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
        <p className="mt-1 text-sm text-zinc-500">Hi {user?.name?.split(" ")[0]} — {sub}</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-blue-600">{c.this_week}</div><div className="text-xs text-zinc-500">This Week</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-red-600">{c.pending_last_week}</div><div className="text-xs text-zinc-500">Carry-forward</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-amber-600">{c.my_in_progress}</div><div className="text-xs text-zinc-500">My In Progress</div></Card>
        <Card className="rounded-lg border-zinc-200 p-3 text-center shadow-sm"><div className="metric-value text-2xl font-semibold text-emerald-600">{c.completed_today}</div><div className="text-xs text-zinc-500">Completed Today</div></Card>
      </div>

      <InProgressNow types={isVideo ? VIDEO_TYPES : DESIGN_TYPES} />

      <PendingWorkTable types={isVideo ? VIDEO_TYPES : DESIGN_TYPES} />

      <UpcomingFestivals days={45} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PoolSection title="This Week" icon={Clock} items={data.this_week} empty="Nothing to work on this week" renderActions={poolActions} />
        <PoolSection title="Pending From Last Week" icon={Clock} tone="red" items={data.pending_last_week} empty="No carry-forward work 🎉" renderActions={poolActions} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PoolSection title="My In Progress" icon={Play} items={data.my_in_progress} empty="Nothing in progress — pick something above" renderActions={(it) => <>{completeBtn(it)}{undoBtn(it)}</>} />
        <PoolSection title="Completed Today" icon={CheckCircle2} items={data.completed_today} empty="No completions logged today yet" />
      </div>

      <DailyWork />
    </div>
  );
}
